import { useEffect, useRef, useState } from "react";
import { FileText, Loader2, Trash2, Upload, Lock } from "lucide-react";
import { toast } from "sonner";
import { uploadDocument } from "@/lib/media";
import { api } from "@/lib/api";

const MAX = 10 * 1024 * 1024;
const OK_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

function PdfThumb({ d }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!d.public_id) return;
    api.get("/media/doc-preview", { params: { public_id: d.public_id } }).then((r) => setSrc(r.data.preview)).catch(() => {});
  }, [d.public_id]);
  if (src) return <img src={src} alt={d.name} className="h-full w-full bg-white object-cover object-top" data-testid={`doc-preview-pdf-${d.key}`} />;
  return <div className="grid h-full place-items-center text-muted-foreground" data-testid={`doc-preview-pdf-${d.key}`}><div className="text-center"><FileText size={30} className="mx-auto text-red-400" /><p className="mt-1 text-[10px] font-bold uppercase">PDF</p></div></div>;
}

function DocCard({ d, onRemove }) {
  const pdf = d.type === "application/pdf";
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5" data-testid={`doc-item-${d.key}`}>
      <div className="relative aspect-[4/3] bg-black/40">
        {pdf ? <PdfThumb d={d} />
          : <img src={d.preview} alt={d.name} className="h-full w-full object-cover" data-testid={`doc-preview-img-${d.key}`} />}
        {d.progress < 100 && !d.public_id && (
          <div className="absolute inset-0 grid place-items-center bg-black/60 text-sm font-bold"><span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {d.progress}%</span></div>
        )}
      </div>
      <div className="flex items-center gap-2 p-2.5">
        <FileText size={15} className="shrink-0 text-[hsl(var(--gold))]" />
        <p className="min-w-0 flex-1 truncate text-xs font-semibold">{d.name}</p>
        <button type="button" onClick={() => onRemove(d.key)} aria-label="Remover documento" data-testid={`doc-remove-${d.key}`}
          className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-red-500/20 hover:text-red-300"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

export function DocumentUploader({ docs, setDocs }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(0);

  const add = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    for (const file of files) {
      if (docs.length + busy >= 5) { toast.error("Até 5 documentos"); break; }
      if (!OK_TYPES.includes(file.type)) { toast.error(`${file.name}: envie PDF ou imagem`); continue; }
      if (file.size > MAX) { toast.error(`${file.name}: máximo de 10 MB`); continue; }
      const key = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const item = { key, name: file.name, type: file.type, preview: URL.createObjectURL(file), progress: 0 };
      setDocs((p) => [...p, item]);
      setBusy((b) => b + 1);
      try {
        const r = await uploadDocument(file, (pct) => setDocs((p) => p.map((x) => (x.key === key ? { ...x, progress: pct } : x))));
        setDocs((p) => p.map((x) => (x.key === key ? { ...x, public_id: r.public_id, progress: 100 } : x)));
      } catch (err) {
        toast.error(`${file.name}: ${err.message}`);
        setDocs((p) => p.filter((x) => x.key !== key));
      } finally {
        setBusy((b) => b - 1);
      }
    }
  };

  const remove = (key) => setDocs((p) => p.filter((x) => x.key !== key));

  return (
    <div className="space-y-3" data-testid="doc-uploader">
      {docs.length > 0 && <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{docs.map((d) => <DocCard key={d.key} d={d} onRemove={remove} />)}</div>}
      {docs.length < 5 && (
        <button type="button" onClick={() => ref.current.click()} data-testid="doc-upload-button"
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 bg-white/5 px-4 py-5 text-sm font-bold transition-colors hover:border-[hsl(var(--gold))]/60 hover:bg-white/10">
          <Upload size={16} className="text-[hsl(var(--gold))]" /> Enviar documentos (PDF ou imagem)
        </button>
      )}
      <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground"><Lock size={12} className="mt-0.5 shrink-0" /> Estatuto, ata de posse ou cartão CNPJ. Até 5 arquivos de 10 MB. Guardados em pasta privada, visível só para a equipe de validação.</p>
      <input ref={ref} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={add} data-testid="doc-file-input" />
    </div>
  );
}
